import React, { useContext, useState, useEffect, useCallback } from 'react';
import { TranslatorContext } from '../../context/Translator';
import { Row, Col } from 'react-bootstrap';
import { PaginationComponent } from '../../components';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import LabelFieldComponent from '../../components/fields/LabelFieldComponent';
import UsersTableComponent from '../../components/tables/UsersTableComponent';
import PageLayout from '../../layouts/PageLayout';
import { employeeAPI } from '../../services';
import { useBranchAPI, useFilterParams } from '../../hooks';
let thead = ['ID', 'Họ tên', 'Username', 'Điện thoại', 'Email', 'Vai trò', 'Chi nhánh', 'Thao tác'];
// #024038 — hồ sơ ứng tuyển có trạng thái RIÊNG (PENDING_REVIEW / REJECTED), không còn mượn
// INACTIVE. Người duyệt lọc thẳng bằng ô Trạng thái ở trên bảng.
const STATUS_ENUM = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING_REVIEW: 'pending_review',
  REJECTED: 'rejected',
};

// #120936 — Tên param trên URL theo khuôn chốt: snake_case, tiếng Anh, số ít. Tên URL KHÔNG
// bắt buộc trùng tên tham số API (#131331): API `src/services/employee.js:17-22` vẫn nhận
// `{search,page,limit,branch_id,status,role}`, còn URL dùng `q`.
// `status` ghi ra luôn CHỮ HOA, nhưng đọc thì không phân biệt hoa thường.
// `branch_id` KHÔNG khai ở đây — nó do BranchContext quản chung cho 37 trang.
const STATUS_VALUES = Object.keys(STATUS_ENUM); // ALL | ACTIVE | INACTIVE | PENDING_REVIEW | REJECTED

// #070005 — ô "SHOW BY" (số dòng/trang). Trước đây dropdown 12/24/36 không có onSelectChange và
// `limit` bị chốt cứng useState(10) → chọn gì cũng không đổi. Nguồn số duy nhất là mảng này: nhãn
// hiển thị là `<n> row`, giá trị dùng để gọi API là số `n`. Mặc định lấy phần tử đầu (12).
const ROW_OPTIONS = [12, 24, 36];

/**
 * #102134 — TAB NHÌN THẤY NGAY, thay cho việc chôn trạng thái trong ô select.
 *
 * Sếp báo: mở link duyệt trong tin Telegram thì *"không thấy tk nhân viên trong inactive, phải
 * bấm all mới thấy"*. Hai nguyên nhân, sửa cả hai:
 *  - Bản admin đang chạy thật chỉ biết ALL/ACTIVE/INACTIVE, nên `?status=PENDING_REVIEW` trong
 *    link rơi vào nhánh "tham số lạ" và bị bỏ, danh sách tụt về mặc định ACTIVE ⇒ trống trơn.
 *    `STATUS_ENUM` đã có PENDING_REVIEW/REJECTED (#024038) là điều kiện cần.
 *  - Nhưng CHỈ có trong ô select vẫn chưa đủ: hồ sơ chờ duyệt là việc phải làm ngay, không thể
 *    bắt người duyệt sổ danh sách ra tìm. Nên đưa lên tab, kèm số lượng.
 */
const STATUS_TABS = [
  { value: 'PENDING_REVIEW', label: 'Chờ duyệt', badge: true },
  { value: 'ACTIVE', label: 'Đang hoạt động' },
  { value: 'INACTIVE', label: 'Chưa kích hoạt' },
  { value: 'REJECTED', label: 'Đã từ chối' },
  { value: 'ALL', label: 'Tất cả' },
];
const FILTER_SPEC = {
  status: {
    default: 'ACTIVE',
    parse: (v) => v.toUpperCase(),
    valid: (v) => STATUS_VALUES.includes(v.toUpperCase()),
  },
  // #131331 — khoá spec là TÊN TRÊN URL, không phải tên tham số API: chốt `q` dùng chung cả 4
  // mảng. `search` giữ làm alias CHỈ-ĐỌC nên link cũ không gãy; tầng API vẫn nhận `search:`.
  q: { default: '', alias: ['search'], debounce: 350 },
  page: { default: 1, parse: Number, valid: (v) => /^\d+$/.test(v) && Number(v) >= 1 },
};

export default function UserListPage() {
  const { t } = useContext(TranslatorContext);
  const { withBranch, isBranchSelected } = useBranchAPI();
  const [tbody, setTbody] = useState([]);
  const [limit, setLimit] = useState(ROW_OPTIONS[0]);
  const [totalItems, setTotalItems] = useState(0);
  // #120936 — cần chọn chi nhánh mới tải được danh sách. Trước đây lỗi này chỉ ra console còn
  // bảng vẫn khoe "showing 1 to 10 of 10 results" dù trống trơn — xem ghi chú ở fetchUsers.
  const [needBranch, setNeedBranch] = useState(false);

  const [filters, setFilters, , { ignored, dismissIgnored }] = useFilterParams(FILTER_SPEC);
  const { status, q, page } = filters;
  // #102134 — số hồ sơ đang chờ duyệt của chi nhánh đang xem. Đếm riêng (không suy từ bảng)
  // vì bảng đang bị phân trang + lọc theo tab khác.
  const [pendingCount, setPendingCount] = useState(0);

  const handlePageChange = (newPage) => {
    setFilters({ page: newPage });
  };

  // #070005 — đổi số dòng/trang. Nhãn option là `<n> row`, tách lấy số nguyên đầu chuỗi. Về trang 1
  // vì tổng số trang đổi theo limit — đang ở trang 5 với limit 12 mà chuyển 36 thì trang 5 có thể
  // không còn tồn tại. fetchUsers phụ thuộc [limit, page] nên sẽ tự gọi lại API với limit mới.
  const handleLimitChange = (e) => {
    const next = parseInt(e.target.value, 10);
    if (!Number.isNaN(next)) {
      setLimit(next);
      setFilters({ page: 1 });
    }
  };

  const fetchUsers = useCallback(async () => {
    // Kiểm TRƯỚC khi gọi: `withBranch` ném lỗi khi chưa chọn chi nhánh, và cái ném đó trước đây
    // bị nuốt vào catch → người dùng không hề biết vì sao bảng trống.
    if (!isBranchSelected()) {
      setNeedBranch(true);
      setTbody([]);
      setTotalItems(0);
      return;
    }

    setNeedBranch(false);
    try {
      const res = await withBranch(employeeAPI.getEmployees, {
        search: q,
        page,
        limit,
        status: status === 'ALL' ? undefined : status,
      });
      const list = res?.data || res?.items || res?.results || res;
      const total = res?.total || res?.meta?.total || (Array.isArray(list) ? list.length : 0);
      setTbody(Array.isArray(list) ? list : []);
      setTotalItems(total);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Thất bại thì phải để bảng rỗng VÀ số đếm về 0, không giữ số cũ làm người xem tưởng có dữ liệu.
      setTbody([]);
      setTotalItems(0);
    }
  }, [withBranch, isBranchSelected, q, page, limit, status]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [fetchUsers]);

  // #102134 — đếm hồ sơ chờ duyệt để gắn số lên tab. Chạy lại sau mỗi lần tải bảng vì duyệt
  // xong là con số phải giảm ngay, không để người dùng nhìn số cũ.
  const fetchPendingCount = useCallback(async () => {
    if (!isBranchSelected()) {
      setPendingCount(0);
      return;
    }
    try {
      const res = await withBranch(employeeAPI.getEmployees, {
        page: 1,
        limit: 1,
        status: 'PENDING_REVIEW',
      });
      setPendingCount(res?.total ?? res?.meta?.total ?? 0);
    } catch (error) {
      // Đếm hỏng thì ẩn con số, KHÔNG hiện 0 giả — số 0 sai khiến người duyệt bỏ sót hồ sơ thật.
      console.error('Error counting pending applicants:', error);
      setPendingCount(null);
    }
  }, [withBranch, isBranchSelected]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount, tbody]);

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">{t('user_list')}</h3>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">
            <InvalidParamsNotice
              ignored={ignored}
              onDismiss={dismissIgnored}
            />
            {/* #102134 — tab trạng thái: bấm 1 phát ra đúng danh sách, không phải mò trong ô select. */}
            <div className="d-flex flex-wrap gap-2 mb-3" role="tablist">
              {STATUS_TABS.map((tab) => {
                const active = String(status).toUpperCase() === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setFilters({ status: tab.value, page: 1 })}
                  >
                    {tab.label}
                    {/* Chỉ gắn số khi ĐẾM ĐƯỢC THẬT: đếm hỏng trả null thì ẩn hẳn, vì một con số
                        "0" sai ở đúng tab này làm người duyệt tưởng hết việc và bỏ sót hồ sơ thật. */}
                    {tab.badge && pendingCount ? (
                      <span className="badge bg-danger ms-2">{pendingCount}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <Row
              xs={1}
              sm={2}
              xl={4}
            >
              <Col>
                <LabelFieldComponent
                  label={t('show_by')}
                  option={ROW_OPTIONS.map((n) => `${n} row`)}
                  labelDir="label-col"
                  fieldSize="mb-4 w-100 h-md"
                  onSelectChange={handleLimitChange}
                  value={`${limit} row`}
                />
              </Col>

              <Col>
                <LabelFieldComponent
                  label={t('status_by')}
                  option={Object.values(STATUS_ENUM)}
                  labelDir="label-col"
                  fieldSize="mb-4 w-100 h-md"
                  onSelectChange={(e) => setFilters({ status: e.target.value.toUpperCase() })}
                  value={String(status).toLowerCase()}
                />
              </Col>
              <Col>
                <LabelFieldComponent
                  type="search"
                  label={t('search_by')}
                  placeholder={t('id') + ' / ' + t('name') + ' / ' + t('email') + ' / ' + t('number')}
                  labelDir="label-col"
                  fieldSize="mb-4 w-100 h-md"
                  value={q}
                  onChange={(e) => setFilters({ q: e.target.value })}
                />
              </Col>
            </Row>

            {needBranch ? (
              // #120936 (luật 3b) — trạng thái rỗng CÓ HƯỚNG DẪN, thay cho lỗi chỉ-ra-console.
              <div
                className="mc-empty-state text-center py-5"
                role="status"
              >
                <i
                  className="icofont-building"
                  style={{ fontSize: 32, opacity: 0.5 }}
                ></i>
                <h5 className="mt-2">Chưa chọn chi nhánh</h5>
                <p className="mb-0">
                  Danh sách nhân viên được lọc theo chi nhánh. Hãy chọn một chi nhánh ở ô{' '}
                  <strong>Chi nhánh</strong> trên thanh đầu trang, hoặc mở thẳng bằng đường dẫn có{' '}
                  <code>?branch_id=&lt;id chi nhánh&gt;</code>.
                </p>
              </div>
            ) : (
              <>
                <UsersTableComponent
                  thead={thead}
                  tbody={tbody}
                  onRefresh={fetchUsers}
                />
                <PaginationComponent
                  itemsPerPage={limit}
                  onPageChange={handlePageChange}
                  currentPage={page}
                  totalItems={totalItems}
                />
              </>
            )}
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}
